/**
 * Supplier Portal Page
 * Manage supplier relationships for value chain
 * 
 * Connected to backend API: GET /api/v1/suppliers
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Factory, Search, Filter, Plus, CheckCircle2, AlertTriangle, Clock,
  ExternalLink, Mail, BarChart3, RefreshCw, ChevronRight, Globe, Shield,
  TrendingUp, Users, X, Phone, FileText, Calendar, Star, History,
  Activity, ArrowUpRight, ArrowDownRight, Package, Truck
} from 'lucide-react';
import { API } from '../../App';
import { apiFetch } from '../../services/api_service';

const RISK_CONFIG = {
  low: { label: 'Low Risk', bg: 'bg-emerald-500/20', text: 'text-emerald-400', score: [0, 40] },
  medium: { label: 'Medium Risk', bg: 'bg-amber-500/20', text: 'text-amber-400', score: [41, 70] },
  high: { label: 'High Risk', bg: 'bg-red-500/20', text: 'text-red-400', score: [71, 100] },
};

const STATUS_CONFIG = {
  active: { label: 'Active', bg: 'bg-emerald-500/20', text: 'text-emerald-400', icon: CheckCircle2 },
  pending_approval: { label: 'Pending Review', bg: 'bg-amber-500/20', text: 'text-amber-400', icon: Clock },
  inactive: { label: 'Inactive', bg: 'bg-[#1F1F1F]', text: 'text-[#6B6B6B]', icon: Clock },
  suspended: { label: 'Suspended', bg: 'bg-red-500/20', text: 'text-red-400', icon: AlertTriangle },
};

const TIER_CONFIG = {
  1: { label: 'Tier 1', color: 'emerald' },
  2: { label: 'Tier 2', color: 'blue' },
  3: { label: 'Tier 3', color: 'purple' },
};

const getRiskLevel = (score) => {
  if (score <= 40) return 'low';
  if (score <= 70) return 'medium';
  return 'high';
};

const SupplierCard = ({ supplier, onClick }) => {
  const riskScore = supplier.risk_profile?.overall_risk_score || 50;
  const riskLevel = getRiskLevel(riskScore);
  const riskConfig = RISK_CONFIG[riskLevel];
  const statusConfig = STATUS_CONFIG[supplier.status] || STATUS_CONFIG.active;
  const tierConfig = TIER_CONFIG[supplier.tier] || TIER_CONFIG[1];
  const StatusIcon = statusConfig.icon;

  return (
    <div 
      className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5 hover:border-emerald-500/30 transition-all group cursor-pointer"
      data-testid={`supplier-card-${supplier.id}`}
      onClick={() => onClick?.(supplier)}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-${tierConfig.color}-500/20 text-${tierConfig.color}-400`}>
              {tierConfig.label}
            </span>
            <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded ${riskConfig.bg} ${riskConfig.text}`}>
              {riskConfig.label}
            </span>
          </div>
          <h3 className="text-lg font-semibold text-white group-hover:text-emerald-400 transition-colors">
            {supplier.supplier_name}
          </h3>
          <div className="flex items-center gap-3 mt-1 text-xs text-[#6B6B6B]">
            <span className="flex items-center gap-1">
              <Globe size={12} />
              {supplier.country}
            </span>
            <span className="capitalize">{supplier.supplier_type}</span>
          </div>
        </div>
        <div className="flex items-center gap-1 text-[#6B6B6B] group-hover:text-emerald-400 transition-colors">
          <span className="text-[10px]">View</span>
          <ChevronRight size={14} />
        </div>
      </div>

      {/* ESG Score */}
      <div className="mb-4 p-3 bg-[#0A0A0A] rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-[#A0A0A0]">ESG Risk Score</span>
          <span className={`text-lg font-bold ${riskConfig.text}`}>{riskScore}</span>
        </div>
        <div className="h-1.5 bg-[#1F1F1F] rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full ${
              riskLevel === 'low' ? 'bg-emerald-500' :
              riskLevel === 'medium' ? 'bg-amber-500' : 'bg-red-500'
            }`}
            style={{ width: `${riskScore}%` }}
          />
        </div>
        <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
          <div className="text-center">
            <div className="text-emerald-400">{supplier.risk_profile?.environmental_score || '-'}</div>
            <div className="text-[#6B6B6B]">E</div>
          </div>
          <div className="text-center">
            <div className="text-blue-400">{supplier.risk_profile?.social_score || '-'}</div>
            <div className="text-[#6B6B6B]">S</div>
          </div>
          <div className="text-center">
            <div className="text-purple-400">{supplier.risk_profile?.governance_score || '-'}</div>
            <div className="text-[#6B6B6B]">G</div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3">
        <div className="text-center p-2 bg-[#0A0A0A] rounded-lg">
          <div className="text-sm font-bold text-white">
            {supplier.categories?.length || 0}
          </div>
          <div className="text-[10px] text-[#6B6B6B] uppercase tracking-wider">Categories</div>
        </div>
        <div className="text-center p-2 bg-[#0A0A0A] rounded-lg">
          <div className="text-sm font-bold text-white">
            {supplier.scope3_categories?.length || 0}
          </div>
          <div className="text-[10px] text-[#6B6B6B] uppercase tracking-wider">Scope 3</div>
        </div>
      </div>

      {/* Contact */}
      {supplier.primary_contact && (
        <div className="mt-4 pt-4 border-t border-[#1F1F1F] flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-[#6B6B6B]">
            <Users size={12} />
            {supplier.primary_contact.name}
          </div>
          <a 
            href={`mailto:${supplier.primary_contact.email}`}
            className="text-emerald-400 hover:text-emerald-300"
          >
            <Mail size={14} />
          </a>
        </div>
      )}
    </div>
  );
};

/* ── Supplier Detail Panel ─────────────────────────────────── */
function SupplierDetailPanel({ supplier, onClose }) {
  const [activeTab, setActiveTab] = useState('overview');
  const riskScore = supplier.risk_profile?.overall_risk_score || 50;
  const riskLevel = getRiskLevel(riskScore);
  const riskConfig = RISK_CONFIG[riskLevel];
  const statusConfig = STATUS_CONFIG[supplier.status] || STATUS_CONFIG.active;
  const tierConfig = TIER_CONFIG[supplier.tier] || TIER_CONFIG[1];
  
  const envScore = supplier.risk_profile?.environmental_score || 0;
  const socScore = supplier.risk_profile?.social_score || 0;
  const govScore = supplier.risk_profile?.governance_score || 0;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FileText },
    { id: 'esg', label: 'ESG Profile', icon: Shield },
    { id: 'scope3', label: 'Scope 3', icon: Activity },
    { id: 'actions', label: 'Actions', icon: CheckCircle2 },
  ];
  
  const scope3CatNames = {
    1: 'Purchased goods & services', 2: 'Capital goods', 3: 'Fuel & energy activities',
    4: 'Upstream transportation', 5: 'Waste in operations', 6: 'Business travel',
    7: 'Employee commuting', 8: 'Upstream leased assets', 9: 'Downstream transportation',
    10: 'Processing of sold products', 11: 'Use of sold products', 12: 'End-of-life treatment',
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="supplier-detail-panel">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-[#0A0A0A] border-l border-[#1F1F1F] shadow-2xl overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#1F1F1F] p-6 z-10">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded bg-${tierConfig.color}-500/20 text-${tierConfig.color}-400`}>
                  {tierConfig.label}
                </span>
                <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded ${riskConfig.bg} ${riskConfig.text}`}>
                  {riskConfig.label} Risk
                </span>
                <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded ${statusConfig.bg} ${statusConfig.text}`}>
                  {statusConfig.label}
                </span>
              </div>
              <h2 className="text-xl font-bold text-white">{supplier.supplier_name}</h2>
              <div className="flex items-center gap-3 mt-1 text-xs text-[#6B6B6B]">
                <span className="flex items-center gap-1"><Globe size={12} /> {supplier.country}</span>
                <span className="capitalize">{(supplier.supplier_type || '').replace('_', ' ')}</span>
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-[#1F1F1F] text-[#6B6B6B] hover:text-white transition-colors" data-testid="close-supplier-panel">
              <X size={20} />
            </button>
          </div>
          <div className="flex gap-1 mt-4">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    activeTab === tab.id ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'text-[#6B6B6B] hover:text-white hover:bg-[#1F1F1F]'
                  }`} data-testid={`supplier-tab-${tab.id}`}
                ><Icon size={12} />{tab.label}</button>
              );
            })}
          </div>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Risk Score Hero */}
              <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-white">Overall Risk Assessment</h3>
                  <span className={`text-3xl font-bold ${riskConfig.text}`}>{riskScore}</span>
                </div>
                <div className="h-2 bg-[#1F1F1F] rounded-full overflow-hidden mb-4">
                  <div className={`h-full rounded-full ${riskLevel === 'low' ? 'bg-emerald-500' : riskLevel === 'medium' ? 'bg-amber-500' : 'bg-red-500'}`}
                    style={{ width: `${riskScore}%` }} />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { label: 'Environmental', score: envScore, color: 'emerald' },
                    { label: 'Social', score: socScore, color: 'blue' },
                    { label: 'Governance', score: govScore, color: 'purple' },
                  ].map(({ label, score, color }) => (
                    <div key={label} className="text-center p-3 bg-[#0A0A0A] rounded-lg">
                      <div className={`text-xl font-bold text-${color}-400`}>{score}</div>
                      <div className="text-[10px] text-[#6B6B6B] uppercase tracking-wider mt-1">{label}</div>
                      <div className="h-1 bg-[#1F1F1F] rounded-full mt-2 overflow-hidden">
                        <div className={`h-full rounded-full bg-${color}-500`} style={{ width: `${score}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Categories & Contact */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
                  <h4 className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-3">Categories</h4>
                  <div className="flex flex-wrap gap-2">
                    {(supplier.categories || []).map(cat => (
                      <span key={cat} className="px-2 py-1 text-xs bg-[#1F1F1F] text-[#A0A0A0] rounded">{cat}</span>
                    ))}
                  </div>
                </div>
                <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
                  <h4 className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-3">Primary Contact</h4>
                  {supplier.primary_contact ? (
                    <div>
                      <div className="text-sm font-medium text-white">{supplier.primary_contact.name}</div>
                      <a href={`mailto:${supplier.primary_contact.email}`} className="flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 mt-1">
                        <Mail size={10} /> {supplier.primary_contact.email}
                      </a>
                    </div>
                  ) : <span className="text-xs text-[#6B6B6B]">No contact set</span>}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'esg' && (
            <div className="space-y-4">
              {[
                { label: 'Environmental', score: envScore, color: 'emerald', items: ['Carbon emissions reporting', 'Waste management', 'Water usage tracking', 'Energy efficiency'] },
                { label: 'Social', score: socScore, color: 'blue', items: ['Labor practices', 'Health & safety', 'Diversity metrics', 'Community engagement'] },
                { label: 'Governance', score: govScore, color: 'purple', items: ['Anti-corruption policy', 'Board diversity', 'Whistleblower protection', 'Tax transparency'] },
              ].map(({ label, score, color, items }) => (
                <div key={label} className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className={`text-sm font-semibold text-${color}-400`}>{label}</h4>
                    <span className={`text-lg font-bold text-${color}-400`}>{score}/100</span>
                  </div>
                  <div className="h-1.5 bg-[#1F1F1F] rounded-full overflow-hidden mb-4">
                    <div className={`h-full rounded-full bg-${color}-500`} style={{ width: `${score}%` }} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {items.map((item, i) => (
                      <div key={i} className="flex items-center gap-2 p-2 bg-[#0A0A0A] rounded-lg text-xs">
                        <CheckCircle2 size={12} className={score > 50 ? `text-${color}-400` : 'text-[#2F2F2F]'} />
                        <span className="text-[#A0A0A0]">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'scope3' && (
            <div className="space-y-4">
              <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
                <h4 className="text-sm font-semibold text-white mb-3">GHG Protocol Categories</h4>
                <div className="space-y-2">
                  {(supplier.scope3_categories || []).map(catId => (
                    <div key={catId} className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-lg hover:border-emerald-500/30 border border-[#1A1A1A] transition-colors cursor-pointer" data-testid={`scope3-cat-${catId}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-xs font-bold">{catId}</div>
                        <span className="text-sm text-white">{scope3CatNames[catId] || `Category ${catId}`}</span>
                      </div>
                      <ChevronRight size={14} className="text-[#6B6B6B]" />
                    </div>
                  ))}
                  {(!supplier.scope3_categories || supplier.scope3_categories.length === 0) && (
                    <div className="text-center py-6 text-[#6B6B6B] text-sm">No Scope 3 categories assigned</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'actions' && (
            <div className="space-y-4">
              {[
                { label: 'Send ESG Survey', desc: 'Request updated ESG data from this supplier', icon: FileText, color: 'emerald' },
                { label: 'Schedule Audit', desc: 'Plan an on-site or remote ESG audit', icon: Calendar, color: 'blue' },
                { label: 'Request Emissions Data', desc: 'Collect Scope 3 emissions data for reporting', icon: Activity, color: 'purple' },
                { label: 'Flag for Review', desc: 'Escalate this supplier for management review', icon: AlertTriangle, color: 'amber' },
              ].map(({ label, desc, icon: Icon, color }) => (
                <button key={label} className={`w-full flex items-center gap-4 p-4 bg-[#111111] border border-[#1F1F1F] rounded-xl hover:border-${color}-500/30 transition-colors text-left`} data-testid={`action-${label.toLowerCase().replace(/ /g, '-')}`}>
                  <div className={`p-3 rounded-lg bg-${color}-500/20`}>
                    <Icon size={18} className={`text-${color}-400`} />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-white">{label}</div>
                    <div className="text-xs text-[#6B6B6B] mt-0.5">{desc}</div>
                  </div>
                  <ChevronRight size={16} className="text-[#6B6B6B]" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SupplierPortalPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRisk, setFilterRisk] = useState('all');
  const [filterTier, setFilterTier] = useState('all');
  const [suppliers, setSuppliers] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  // Fetch suppliers from backend
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Fetch suppliers
        const suppliersData = await apiFetch('/suppliers/list?limit=50');
        if (suppliersData?._error) throw new Error('Failed to fetch suppliers');
        setSuppliers(suppliersData.items || []);

        // Fetch statistics
        try {
          const statsData = await apiFetch('/suppliers/statistics');
          if (!statsData?._error) setStatistics(statsData);
        } catch (e) {}
      } catch (err) {
        console.error('Error fetching suppliers:', err);
        setError(err.message);
        // Fallback mock data
        setSuppliers([
          {
            id: '1',
            supplier_name: 'TechParts Manufacturing Co.',
            tier: 1,
            country: 'Germany',
            supplier_type: 'manufacturer',
            categories: ['Electronics', 'Components'],
            scope3_categories: [1, 4],
            risk_profile: { overall_risk_score: 25, environmental_score: 30, social_score: 20, governance_score: 25 },
            status: 'active',
            primary_contact: { name: 'Hans Mueller', email: 'hans@techparts.de' },
          },
          {
            id: '2',
            supplier_name: 'Global Logistics Partners',
            tier: 1,
            country: 'Netherlands',
            supplier_type: 'service_provider',
            categories: ['Transportation', 'Logistics'],
            scope3_categories: [4, 9],
            risk_profile: { overall_risk_score: 55, environmental_score: 60, social_score: 50, governance_score: 55 },
            status: 'active',
            primary_contact: { name: 'Anna van Berg', email: 'anna@glp.nl' },
          },
          {
            id: '3',
            supplier_name: 'Raw Materials Inc.',
            tier: 2,
            country: 'Brazil',
            supplier_type: 'raw_material',
            categories: ['Raw Materials', 'Mining'],
            scope3_categories: [1, 2],
            risk_profile: { overall_risk_score: 78, environmental_score: 85, social_score: 75, governance_score: 74 },
            status: 'pending_approval',
            primary_contact: { name: 'Carlos Silva', email: 'carlos@rawmaterials.br' },
          },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredSuppliers = suppliers.filter(supplier => {
    const matchesSearch = supplier.supplier_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         supplier.country?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const riskScore = supplier.risk_profile?.overall_risk_score || 50;
    const riskLevel = getRiskLevel(riskScore);
    const matchesRisk = filterRisk === 'all' || riskLevel === filterRisk;
    
    const matchesTier = filterTier === 'all' || supplier.tier === parseInt(filterTier);
    
    return matchesSearch && matchesRisk && matchesTier;
  });

  // Calculate stats
  const totalSuppliers = suppliers.length;
  const activeSuppliers = suppliers.filter(s => s.status === 'active').length;
  const highRiskCount = suppliers.filter(s => getRiskLevel(s.risk_profile?.overall_risk_score || 50) === 'high').length;
  const avgEsgScore = suppliers.length > 0 
    ? Math.round(suppliers.reduce((sum, s) => sum + (s.risk_profile?.overall_risk_score || 50), 0) / suppliers.length)
    : 0;

  return (
    <div className="space-y-6" data-testid="supplier-portal-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Supplier Portal</h1>
          <p className="text-sm text-[#6B6B6B] mt-1">
            Manage supplier relationships and ESG compliance
          </p>
        </div>
        <Link
          to="/value-chain/suppliers/new"
          className="flex items-center gap-2 px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 rounded-lg transition-colors"
          data-testid="add-supplier-btn"
        >
          <Plus size={16} />
          <span className="text-sm font-medium">Add Supplier</span>
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/20">
              <Factory size={18} className="text-emerald-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{totalSuppliers}</div>
              <div className="text-xs text-[#6B6B6B]">Total Suppliers</div>
            </div>
          </div>
        </div>
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <CheckCircle2 size={18} className="text-blue-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{activeSuppliers}</div>
              <div className="text-xs text-[#6B6B6B]">Active</div>
            </div>
          </div>
        </div>
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-500/20">
              <AlertTriangle size={18} className="text-red-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{highRiskCount}</div>
              <div className="text-xs text-[#6B6B6B]">High Risk</div>
            </div>
          </div>
        </div>
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/20">
              <BarChart3 size={18} className="text-purple-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{avgEsgScore}</div>
              <div className="text-xs text-[#6B6B6B]">Avg Risk Score</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
          <input
            type="text"
            placeholder="Search suppliers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-[#111111] border border-[#1F1F1F] rounded-lg text-sm text-white placeholder:text-[#6B6B6B] focus:border-emerald-500/50 focus:outline-none"
            data-testid="supplier-search-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-[#6B6B6B]" />
          <select
            value={filterRisk}
            onChange={(e) => setFilterRisk(e.target.value)}
            className="px-3 py-2 bg-[#111111] border border-[#1F1F1F] rounded-lg text-sm text-white focus:border-emerald-500/50 focus:outline-none"
          >
            <option value="all">All Risk Levels</option>
            <option value="low">Low Risk</option>
            <option value="medium">Medium Risk</option>
            <option value="high">High Risk</option>
          </select>
          <select
            value={filterTier}
            onChange={(e) => setFilterTier(e.target.value)}
            className="px-3 py-2 bg-[#111111] border border-[#1F1F1F] rounded-lg text-sm text-white focus:border-emerald-500/50 focus:outline-none"
          >
            <option value="all">All Tiers</option>
            <option value="1">Tier 1</option>
            <option value="2">Tier 2</option>
            <option value="3">Tier 3</option>
          </select>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw size={24} className="text-emerald-400 animate-spin" />
          <span className="ml-3 text-[#A0A0A0]">Loading suppliers...</span>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
          <div className="flex items-center gap-2 text-amber-400 text-sm">
            <AlertTriangle size={16} />
            <span>Using cached data. API connection issue: {error}</span>
          </div>
        </div>
      )}

      {/* Suppliers Grid */}
      {!loading && (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredSuppliers.map(supplier => (
            <SupplierCard key={supplier.id} supplier={supplier} onClick={setSelectedSupplier} />
          ))}
        </div>
      )}

      {!loading && filteredSuppliers.length === 0 && (
        <div className="text-center py-12 bg-[#111111] border border-[#1F1F1F] rounded-xl">
          <Factory size={48} className="mx-auto text-[#2F2F2F] mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No suppliers found</h3>
          <p className="text-sm text-[#6B6B6B] mb-4">
            {searchQuery || filterRisk !== 'all' || filterTier !== 'all'
              ? 'Try adjusting your search or filter criteria'
              : 'Add your first supplier to get started'}
          </p>
          {!searchQuery && filterRisk === 'all' && filterTier === 'all' && (
            <Link 
              to="/value-chain/suppliers/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 rounded-lg transition-colors"
            >
              <Plus size={16} />
              Add Supplier
            </Link>
          )}
        </div>
      )}

      {/* Scope 3 Summary Link */}
      <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-white">Scope 3 Emissions Summary</h3>
            <p className="text-sm text-[#6B6B6B] mt-1">
              View emissions data by GHG Protocol category
            </p>
          </div>
          <Link 
            to="/value-chain/scope3"
            className="flex items-center gap-2 px-4 py-2 bg-[#1F1F1F] hover:bg-emerald-500/20 text-[#A0A0A0] hover:text-emerald-400 rounded-lg transition-colors"
          >
            View Scope 3
            <ChevronRight size={16} />
          </Link>
        </div>
      </div>

      {/* Detail Panel */}
      {selectedSupplier && (
        <SupplierDetailPanel
          supplier={selectedSupplier}
          onClose={() => setSelectedSupplier(null)}
        />
      )}
    </div>
  );
}
